import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaBullhorn, 
  FaWaveSquare, 
  FaGraduationCap, 
  FaArrowRight,
  FaStar,
  FaQuoteLeft,
  FaCheckCircle
} from 'react-icons/fa';
import './Home.css';
import qrImage from '../assets/images/maqr.jpeg';

// Import images from assets
import speakerImage from '../assets/images/MARKAUDIOXT-12.png';
import amplifierImage from '../assets/images/amplifier.png';
import mixerImage from '../assets/images/mixer.png';

// Import videos from assets
import barberVideo from '../assets/videos/barber-desktop.webm';
import ceramicsVideo from '../assets/videos/ceramics-desktop.webm';
import plantsVideo from '../assets/videos/plants-tablet.webm';

const Home = () => {
  // Hero carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videoRef = useRef(null);
  
  const heroVideos = [plantsVideo, ceramicsVideo, barberVideo];
  
  const heroImages = [
    speakerImage,
    amplifierImage,
    mixerImage, 
    speakerImage
  ];

  // Scroll animation state
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections with animation
    const sections = document.querySelectorAll('.services, .about-preview, .certifications, .testimonials, .cta-section, .brands');
    sections.forEach(section => observer.observe(section));

    // Observe individual cards/items
    const animatedElements = document.querySelectorAll('.service-card, .certification-card, .testimonial-card, .about-content, .about-image');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Auto-change slides - handles both video completion and image rotation
  useEffect(() => {
    // Only start timer for image slides (not video slide at index 0)
    if (currentImageIndex === 0) {
      return; // Videos handle their own progression
    }

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => {
        // After last image, go back to video slide
        if (prevIndex === heroImages.length - 1) {
          setCurrentVideoIndex(0); // Reset to first video
          return 0; // Go to video slide
        }
        return prevIndex + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [currentImageIndex, heroImages.length]);

  // Handle video ended - play next video or move to images
  const handleVideoEnded = () => {
    if (currentVideoIndex < heroVideos.length - 1) {
      // More videos to play, go to next video
      setCurrentVideoIndex((prevIndex) => prevIndex + 1);
    } else {
      // All videos played, move to first image slide
      setCurrentVideoIndex(0); // Reset for next time
      setCurrentImageIndex(1); // Move to first image slide (index 1)
    }
  };

  // Change video source when currentVideoIndex changes
  useEffect(() => {
    if (videoRef.current && currentImageIndex === 0) {
      const video = videoRef.current;
      video.src = heroVideos[currentVideoIndex];
      video.load();
      
      // Play the video and handle autoplay restrictions
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.log('Video autoplay prevented:', err);
          // Attempt to play on user interaction
          document.addEventListener('click', () => {
            video.play().catch(e => console.log('Play failed:', e));
          }, { once: true });
        });
      }
    }
  }, [currentVideoIndex, currentImageIndex, heroVideos]);

  // Restart video when navigating back to video slide
  useEffect(() => {
    if (currentImageIndex === 0 && videoRef.current) {
      videoRef.current.play().catch(err => console.log('Video play prevented:', err));
    }
  }, [currentImageIndex]);

  // Services data
  const services = [
    {
      icon: <FaBullhorn />,
      title: 'PA System Design & Installation',
      description: 'Professional Public Address systems for conferences, auditoriums, theaters, and commercial spaces. We deliver crystal-clear sound reinforcement solutions tailored to your venue\'s unique acoustics and requirements for optimal audio coverage.',
      link: '/services/pa-system',
      image: 'https://images.unsplash.com/photo-1598653222000-6b7b7a552625?w=800&h=600&fit=crop&q=80' // PA System
    },
    {
      icon: <FaWaveSquare />,
      title: 'Acoustic Solutions',
      description: 'Professional acoustic design, treatment, and soundproofing solutions for recording studios, home theaters, offices, and auditoriums. We combine science and art to create acoustically perfect environments for any application.',
      link: '/services/acoustic-solutions',
      image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&h=600&fit=crop&q=80' // Acoustic treatment
    },
    {
      icon: <FaGraduationCap />,
      title: 'Training & Service',
      description: 'Comprehensive training programs and service support for all your audio equipment. We empower your team with knowledge and provide reliable maintenance services to ensure your systems deliver consistent, high-quality performance.',
      link: '/services/training-service',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop&q=80' // Training session
    }
  ];

  // Brands data - using actual SoundInnovators brand logos
  const brands = [
    { name: 'Artcoustic', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/Artcoustic-logo.svg' },
    { name: 'Bowers & Wilkins', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/Bowers-Wilkins-Logo.svg' },
    { name: 'Dali', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/Dali-logo.svg' },
    { name: 'Focal', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/Focal-logo.svg' },
    { name: 'GoldenEar', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/Golden-Ear-logo.svg' },
    { name: 'JL Audio', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/JL-Audio-logo.svg' },
    { name: 'KEF', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/KEF-logo.svg' },
    { name: 'McIntosh', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/McIntosh-logo.svg' },
    { name: 'Paradigm', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/Paradigm-logo.svg' },
    { name: 'REL', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/REL-logo.svg' },
    { name: 'Rotel', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/Rotel-logo.svg' },
    { name: 'Sonus faber', logo: 'https://soundinnovators.in/wp-content/uploads/2023/10/Sonus-faber-logo.svg' }
  ];

  // Testimonials data
  const testimonials = [
    {
      name: 'Rajesh Kumar',
      rating: 5,
      text: 'Exceptional service! The team installed a complete home theater system in our residence. The sound quality is absolutely mind-blowing. Highly recommended!',
      location: 'Guntur',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&q=80' // Professional man
    },
    {
      name: 'Priya Sharma',
      rating: 5,
      text: 'F&H Audio Solutions transformed our living room with their Hi-Fi audio system. Professional, courteous, and extremely knowledgeable. Worth every penny!',
      location: 'Vijayawada',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&q=80' // Professional woman
    },
    {
      name: 'Amit Patel',
      rating: 5,
      text: 'The home automation installation was seamless. Now we control everything from our phones. The lighting and audio integration is fantastic. Great work!',
      location: 'Hyderabad',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&q=80' // Professional man
    }
  ];

  // Certifications
  const certifications = [
    { name: 'Technical Specialist', image: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=400&h=300&fit=crop&q=80' }, // Certificate/Award
    { name: 'Audio Expert', image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&h=300&fit=crop&q=80' }, // Professional certification
    { name: 'Installation Pro', image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&h=300&fit=crop&q=80' }, // Professional badge
    { name: 'Acoustic Consultant', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop&q=80' } // Team/Professional
  ];

  return (
    <div className="home">
      {/* Hero Section with Carousel */}
      <section className="hero">
        {/* Image/Video Carousel */}
        <div className="hero-carousel">
          {/* First slide with video */}
          {currentImageIndex === 0 && (
            <video
              ref={videoRef}
              className="hero-video active"
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnded}
            >
              <source src={heroVideos[currentVideoIndex]} type="video/webm" />
              Your browser does not support the video tag.
            </video>
          )}
          
          {/* Other slides with images */}
          {heroImages.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Hero ${index + 1}`}
              className={`hero-image ${index === currentImageIndex && index !== 0 ? 'active' : ''}`}
              style={{ display: index === 0 ? 'none' : 'block' }}
            />
          ))}
        </div>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
           {/* Delivering Precision-Engineered Audio Solutions Backed By 6+ Years Of Proven Expertise In Acoustic Consultancy, System Architecture, And Professional Installation. */}
          </h1>
          {/* <p className="hero-subtitle">
We combine smart technology and expert guidance to build the audio-video setup you need — clear, powerful, and professionally executed.</p> */}
          <div className="hero-buttons">
            {/* <a href="tel:+919876543210" className="btn btn-primary">
              Book Appointment
            </a>
            <Link to="/services/hifi-audio" className="btn btn-secondary">
              Explore Our Services
            </Link> */}
          </div>
        </div>
        
        {/* Carousel Navigation Dots */}
        <div className="carousel-dots">
          {heroImages.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentImageIndex ? 'active' : ''}`}
              onClick={() => {
                setCurrentImageIndex(index);
                // If clicking on video slide, reset to first video
                if (index === 0) {
                  setCurrentVideoIndex(0);
                }
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
       
      </section>

      {/* Services Section */}
      <section className="services">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">WHAT WE DO?</h2>
            <p className="section-subtitle">
             We analyze your venue, recommend the right sound system, and provide expert installation — taking care of everything end-to-end. We also train your team to operate the PA system easily and effectively.
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div className="service-card" key={index}>
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                 
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <Link to={service.link} className="service-link">
                    Read More <FaArrowRight />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section - Two Column Carousel */}
      <section className="brands">
        <div className="brands-two-column-wrapper">
          
          {/* Left Column - OUR PRODUCTS */}
          <div className="brands-column-container">
            <div className="section-header">
              <h2 className="section-title">OUR PRODUCTS</h2>
              <p className="section-subtitle">
                High-quality audio products designed to deliver clear, powerful, and reliable sound.
              </p>
            </div>
            <div className="brands-carousel">
              <div className="brands-track">
                {/* Duplicate brands array twice for seamless infinite loop */}
                {[...brands, ...brands].map((brand, index) => (
                  <div className="brand-slide" key={`products-${index}`}>
                    <img src={brand.logo} alt={brand.name} />
                  </div>
                ))}
              </div>
            </div>
            <div className="home-brands-cta">
              <Link to="/contact" className="btn btn-primary">
                View All Products
              </Link>
            </div>
          </div>

          {/* Right Column - PARTNER BRANDS */}
          <div className="brands-column-container">
            <div className="section-header">
              <h2 className="section-title">PARTNER BRANDS</h2>
              <p className="section-subtitle">
                We partner with world-renowned audio brands to deliver exceptional quality and performance.
              </p>
            </div>
            <div className="brands-carousel">
              <div className="brands-track">
                {/* Duplicate brands array twice for seamless infinite loop */}
                {[...brands, ...brands].map((brand, index) => (
                  <div className="brand-slide" key={`brands-${index}`}>
                    <img src={brand.logo} alt={brand.name} />
                  </div>
                ))}
              </div>
            </div>
            <div className="home-brands-cta">
              <Link to="/brands" className="btn btn-primary">
                View All Brands
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview">
        <div className="container">
          <div className="about-grid">
            <div className="about-image">
              <img 
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&h=600&fit=crop&q=80" 
                alt="F&H Audio Solutions"
              />
            </div>
            <div className="about-content">
              <h2 className="section-title">Our Story</h2>
              <p>
                Welcome to F&H Audio Solutions LLP,Founded in 2018 in Guntur, Andhra Pradesh, 
                with brand "MARK AUDIO" which has built a strong name for providing the best and most suitable sound systems for Prayer Halls, schools, and auditoriums.
                By staying updated with the latest global technology, we have successfully completed over 400+ sound system installations.
              </p>
              <p>
                In these six years, Mark Audio has become a trusted partner for Prayer Halls and organizations that want to upgrade their audio systems. 
                Our skilled team carefully studies the structure of each Hall or auditorium and designs a system that fits the space perfectly.
                This ensures clear, warm, and powerful sound for every listener.
              </p>
              <p>
              
              </p>
              <h4> MARK AUDIO — Your Trusted Partner for Professional Sound.</h4>
              <div className="about-features">
                <div className="feature">
                  <FaCheckCircle />
                  <span>7+ Years of Experience</span>
                </div>
                <div className="feature">
                  <FaCheckCircle />
                  <span>Professional Experts</span>
                </div>
                <div className="feature">
                  <FaCheckCircle />
                  <span>Premium Brands</span>
                </div>
                <div className="feature">
                  <FaCheckCircle />
                  <span>Custom Solutions</span>
                </div>
              </div>
              <Link to="/about" className="btn btn-primary">
                More About F&H Audio Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="certifications">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Our Team?</h2>
            <p className="section-subtitle">
              Discover Our Accredited Mastery in Home theater and HiFi audio system Technology from Industry-Leading Brands
            </p>
          </div>
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div className="certification-card" key={index}>
                <img src={cert.image} alt={cert.name} />
                <h4>{cert.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What Our Clients Say</h2>
            <div className="rating-badge">
              <span className="rating-text">EXCELLENT</span>
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <span className="rating-count">Based on 49 reviews</span>
            </div>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div className="testimonial-card" key={index}>
                <FaQuoteLeft className="quote-icon" />
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <p className="testimonial-text">{testimonial.text}</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    <img src={testimonial.image} alt={testimonial.name} />
                  </div>
                  <div className="author-info">
                    <h4>{testimonial.name}</h4>
                    <div className="google-badge">
                      <img src="https://cdn.trustindex.io/assets/platform/Google/icon.svg" alt="Google" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Rating Section */}
      <section className="google-rating-section">
        <div className="container">
          <div className="rating-content">
            <div className="rating-left">
              <h2>Love Our Service?</h2>
              <p>Share your experience and help others discover us!</p>
            </div>
            <div className="rating-right">
              <a 
                href="https://search.google.com/local/writereview?placeid=ChIJr-9VOp91SjoR40D30zYfFIs&source=g.page.m.ia._&utm_source=gbp&laa=nmx-review-solicitation-ia2" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="google-review-btn"
              >
                <img src="https://cdn.trustindex.io/assets/platform/Google/icon.svg" alt="Google" />
                <span>Rate us on Google</span>
                <div className="stars-preview">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
              </a>
              
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Turn Dreams into Reality! Elevate Your Entertainment with Our Expert Home Audio Visual Consultants</h2>
            <p>Discover Seamless Home Audio Visual Consultants and Automation Solutions – Your Personal Cinema, Tailored to Perfection!</p>
            <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-large">
              Schedule Your Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
